'use memo'

import { useCallback, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { App } from 'antd'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import {
  filterDivisionsByRole,
  isDivisionTeamFull,
  soleRoleAcrossDivisions,
} from '@sportninja/common/features/registration/startStep/divisionRole'
import {
  findDivisionTeamById,
  teamsFromRegistration,
} from '@sportninja/common/features/registration/startStep/divisionTeamIndex'
import {
  shouldRestoreSessionDivision,
  shouldRestoreSessionTeam,
} from '@sportninja/common/features/registration/startStep/divisionRestore'
import { teamIdentity } from '@sportninja/common/features/registration/startStep/teamDisplay'
import {
  searchDivisions,
  searchDivisionTeams,
} from '@sportninja/common/features/registration/startStep/searchByName'
import type {
  DivisionTeam,
  StartRole,
} from '@sportninja/common/features/registration/startStep/types'
import { startRoleFor } from '@sportninja/common/features/registration/startStep/startRole'
import { resolveDefaultEntityTab } from '@sportninja/common/features/registration/startStep/defaultEntityTab'
import type { Division } from '@sportninja/common/types'
import { useDivisionFiltering } from 'src/hooks/useDivisionFiltering'
import { useIsMobile } from 'src/hooks/useIsMobile'
import {
  REGISTRATION_DIVISION_PARAM,
  REGISTRATION_ROLE_PARAM,
  REGISTRATION_TEAM_PARAM,
} from '@sportninja/common/features/registration/registrationUrlParams'
import { RegistrationContext } from '../../../context/RegistrationContext'
import { withSelectionParams } from '../../../utils/selectionParams'
import { linkedTeamOutcome } from '../utils/linkedTeamOutcome'
import { committedSelection } from '../utils/committedSelection'
import { usePreselectedDivision } from '../../../hooks/usePreselectedDivision'
import { usePreselectedRole } from '../../../hooks/usePreselectedRole'
import { REGISTRATION_STEPS } from '../../Main'
import { checkIfUserIsVerified } from '../../New_ConfirmYourEmail'
import { usePreselectedStartTeam } from './usePreselectedStartTeam'
import type { EntityTab, EntityViewMode } from '../components/EntityZone'

/**
 * What `PUT /registration/session/:id` rejects with. A 410 means the session is
 * gone and cannot be recovered in place; `invalid_fields` carries per-field
 * messages when the step itself is refused.
 */
interface SessionUpdateError {
  status?: number
  invalid_fields?: { registration_step?: string }
}

/** Stable empty fallback — see `allDivisions`. */
const EMPTY_DIVISIONS: Division[] = []

/**
 * All state and handlers for the merged start step.
 *
 * The view stays declarative: everything about "what is known, and therefore
 * what still needs asking" is decided here, on top of the pure helpers in
 * `common/features/registration/startStep`.
 */
export const useRegistrationStart = () => {
  const {
    registration,
    session,
    isLogged,
    email,
    userId,
    currencyCode,
    genderFilters,
    ageFilters,
    dayOfWeekFilters,
    division,
    setDivision,
    role,
    setRole,
    playerType,
    teamOfficialType,
    setSelectedPlayer,
    // Named apart from this step's own LOCAL `setSelectedTeam`: the context copy
    // is what the register step renders, and is written only on Next.
    setSelectedTeam: setContextSelectedTeam,
    updateSessionDivision,
    updateSessionStart,
    setStep,
    deleteSession,
    resetSession,
    logout,
  } = useContext(RegistrationContext)

  const { message } = App.useApp()
  const history = useHistory()
  const isMobile = useIsMobile()

  // ---- deep links ----------------------------------------------------------

  // Whether the URL names a division, a team or a role. Read once per render
  // from the live location rather than latched: the pre-selects own the "apply
  // it once" decision, while this only says a link is present, which cannot
  // change without a navigation.
  //
  // Declared up here because `setRoleKey` below rewrites `?type=` on a manual
  // role change, and a `const` used before its declaration is a temporal dead
  // zone — it would throw on every render, not just when the param is present.
  const linkParams = new URLSearchParams(history.location.search)
  const hasTeamDeepLink = Boolean(linkParams.get(REGISTRATION_TEAM_PARAM))
  const hasDivisionDeepLink = Boolean(
    linkParams.get(REGISTRATION_DIVISION_PARAM)
  )
  const hasRoleDeepLink = Boolean(linkParams.get(REGISTRATION_ROLE_PARAM))
  const hasSelectionDeepLink = hasTeamDeepLink || hasDivisionDeepLink

  const [selectedAge, setSelectedAge] = useState<string | null>(null)
  const [selectedGender, setSelectedGender] = useState<string | null>(null)
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<string | null>(
    null
  )
  // What the competition's admin chose as the opening tab, or `'teams'` while
  // the payload is still in flight and for a backend that does not send the key.
  //
  // Read on every render rather than latched: `registration` arrives async, so
  // the first render always sees the fallback — the effect below is what
  // applies the real answer once it lands.
  const defaultTab: EntityTab = resolveDefaultEntityTab(
    registration?.registration?.default_registration_tab
  )

  // Teams is the opening tab unless the competition says otherwise: it lists
  // every team until a division narrows it, so it is the one tab that is never
  // empty on arrival. The role effect below moves a team rep to Divisions once
  // their role is known — this is only what shows before that.
  const [tab, setTab] = useState<EntityTab>(defaultTab)
  const [query, setQuery] = useState('')
  //  Cards by default: they carry the fees, badges and crests that make a
  //  choice, and they are the layout that survives a phone.
  const [chosenViewMode, setChosenViewMode] = useState<EntityViewMode>('card')
  const [selectedTeam, setSelectedTeam] =
    useState<DivisionTeam<Division> | null>(null)
  // The reader cleared the division themselves. The session still holds the old
  // one until the next Next, and the restore effect below would otherwise put it
  // straight back — so a deliberate clear has to outrank a stale session value.
  const [divisionCleared, setDivisionCleared] = useState(false)
  // Same reasoning as `divisionCleared`, tracked separately because the team can
  // be dropped while the division stays — switching divisions clears the team
  // that belonged to the old one.
  const [teamCleared, setTeamCleared] = useState(false)
  const [scrollKey, setScrollKey] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  /**
   * The name of a full team a `?team_id=` link named, while the reader is being
   * told about it. Null when there is nothing to report.
   *
   * The NAME rather than a boolean, so the notice can say which team was
   * refused — a reader who followed someone else's link may not otherwise know
   * what it pointed at.
   */
  const [fullLinkedTeamName, setFullLinkedTeamName] = useState<string | null>(
    null
  )

  // ---- divisions -----------------------------------------------------------

  // A module-level constant, NOT a fresh `[]` per render: this feeds
  // `useDivisionFiltering`'s memo, whose result feeds the session-restore
  // effect's dependency array. A new array each render busts the memo, re-runs
  // the effect, re-renders — an infinite loop that only appears while
  // `registration.divisions` is still absent.
  const allDivisions: Division[] = registration?.divisions ?? EMPTY_DIVISIONS

  // The existing age/gender/day filtering, unchanged.
  const filteredDivisions: Division[] = useDivisionFiltering(
    allDivisions,
    selectedAge,
    selectedGender,
    selectedDayOfWeek
  )

  // ---- role ----------------------------------------------------------------

  // The shared rule, so this step and the register step's session restore
  // cannot disagree about which role the reader holds.
  const roleKey: StartRole | null = startRoleFor(
    role,
    playerType,
    teamOfficialType
  )

  const setRoleKey = useCallback(
    (next: StartRole) => {
      const isChange = Boolean(roleKey) && next !== roleKey

      setRole(next === 'player' ? playerType : teamOfficialType)

      /*
       * A different role is a different question, so its answers go.
       *
       * The two roles do not even finish in the same place — a player needs a
       * team, a rep only a division — so a selection made under one is not an
       * answer to the other. Both cleared flags are set with it: the session
       * still holds the old choice until the next Next, and the restore effects
       * would otherwise put it straight back.
       *
       * Only on a genuine SWITCH. Pressing the role you already hold, or
       * answering the question for the first time, must not throw away a
       * division a link or a session just supplied.
       *
       * Cleared here rather than in an effect on `roleKey`: an effect would
       * also fire when `?type=` applies and when a sole role is auto-adopted,
       * wiping selections the URL or the session had just restored. This runs
       * only when a reader presses a role card.
       */
      if (isChange) {
        setDivision(null)
        setSelectedTeam(null)
        setDivisionCleared(true)
        setTeamCleared(true)
        setQuery('')
        setScrollKey((key) => key + 1)
        // The person being registered goes too — a team official chosen as a
        // rep is not one of the reader's players, and the reverse. Left in
        // place, the register step saw someone already chosen, skipped its
        // create-new default, and prefilled (and locked) the new role's name
        // fields from the old role's person, while the dropdown — unable to
        // find them in the new list — displayed "Create a New Player". The
        // same clear `usePreselectedRole` makes when a link's role conflicts.
        setSelectedPlayer(null)
      }

      // Keep an EXISTING `?type=` honest, the same rule `handleNext` follows for
      // all three params: a URL that still named the link's role after the
      // reader chose the other one would survive a refresh and be copied onward,
      // sending the next person to a role nobody picked. A reader who arrived
      // without the param keeps a clean URL — `withSelectionParams` writes only
      // what is already there.
      //
      // Replace, not push: it annotates the step the reader is on rather than
      // adding a history entry they would press Back through twice.
      if (hasRoleDeepLink) {
        history.replace({
          search: withSelectionParams(
            history.location.search,
            { role: next },
            { role: true }
          ),
        })
      }
    },
    // `setDivision` and `setSelectedPlayer` are omitted for the reason given on
    // the effects below: they are RegistrationContext setters, redefined inline
    // on every render, so listing them would rebuild this callback on every pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRole, playerType, teamOfficialType, hasRoleDeepLink, history, roleKey]
  )

  // A registration whose every division offers one side only has no role
  // question worth asking.
  const soleRole = soleRoleAcrossDivisions(filteredDivisions)
  const playerAvailable = filteredDivisions.some(
    (d) => d.is_player_registration
  )
  const teamAvailable = filteredDivisions.some((d) => d.is_team_registration)
  const nothingAvailable = !playerAvailable && !teamAvailable

  // Adopt the only role on offer, so the reader is never asked a question with
  // a single answer.
  useEffect(() => {
    if (soleRole && !roleKey) {
      setRoleKey(soleRole)
    }
    // `setRoleKey` is omitted: it is rebuilt every render because it closes over
    // RegistrationContext's setters, which the provider defines inline. Listing
    // it re-runs this effect on every render, and the effect sets state — an
    // update loop. Its behaviour is stable even though its identity is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soleRole, roleKey])

  /**
   * Cards or rows.
   *
   * Forced to cards on a phone rather than merely defaulted: a grid of eight
   * columns has no honest phone rendering, so the toggle is hidden there and a
   * choice made on a wide screen does not follow the reader down to a narrow
   * one.
   */
  const viewMode: EntityViewMode = isMobile ? 'card' : chosenViewMode

  const selectedDivision: Division | null = division ?? null

  // `?type=` pre-selects the role and nothing more. The hook's lockout flags
  // are deliberately dropped: on this step a link highlights an answer, it does
  // not take one away — same as the division and team pre-selects. The reader
  // can still choose the other side.
  usePreselectedRole({
    playerType,
    teamOfficialType,
    playerRegistrationEnabled: playerAvailable,
    teamRegistrationEnabled: teamAvailable,
    teamRegistrationFull: isDivisionTeamFull(selectedDivision),
    role,
    setRole,
    setSelectedPlayer,
  })

  // ---- the lists the two tabs render ---------------------------------------

  const roleFilteredDivisions = filterDivisionsByRole(
    filteredDivisions,
    roleKey
  )
  const visibleDivisions = searchDivisions(roleFilteredDivisions, query)

  // Every registered team, each carrying its own division. The single source
  // for both tabs and for the `?team_id=` link.
  const fullTeamIndex = teamsFromRegistration(registration?.teams, roleKey)

  // Once a division is chosen the teams tab narrows to it — "find the division,
  // then the team within it". Clearing the division widens it again.
  const teamIndex = selectedDivision
    ? fullTeamIndex.filter((entry) => entry.division.id === selectedDivision.id)
    : fullTeamIndex
  const teamResults = searchDivisionTeams(teamIndex, query)

  // ---- selection handlers --------------------------------------------------

  const handleSelectDivision = useCallback(
    (next: Division) => {
      const isSame = selectedDivision?.id === next.id
      setDivision(isSame ? null : next)
      setDivisionCleared(isSame)

      if (isSame) {
        // Deselecting. The team belongs to the division being cleared, so it
        // goes too — a team outliving its own division would leave the step
        // "answered" with nothing to show for it. Stay on the grid so the
        // reader can pick another.
        setSelectedTeam(null)
        setTeamCleared(true)
        return
      }

      // Switching divisions: the chosen team belongs to the one being left.
      if (selectedTeam?.division?.id !== next.id) {
        setSelectedTeam(null)
        setTeamCleared(true)
      }

      // Choosing a division answers half the question; the other half is which
      // team within it. Move straight there rather than making the reader find
      // the tab themselves — the list is now scoped to what they just picked.
      //
      // Unless there is nothing to move to — dropping the reader onto an empty
      // tab reads as a dead end rather than progress. Counted from the same
      // index the tab renders, so this cannot disagree with what they would
      // actually see there.
      //  ...and not for a REP at all: the teams tab is not theirs to visit, so
      //  advancing to it would move them somewhere they cannot act. Reading
      //  `roleKey` rather than `isTeamRole`, which is declared far below this
      //  and would be a temporal dead zone here.
      const hasTeams =
        roleKey !== 'team' &&
        fullTeamIndex.some((entry) => entry.division.id === next.id)
      setQuery('')
      setScrollKey((key) => key + 1)
      if (hasTeams) {
        setTab('teams')
      }
    },
    [
      selectedDivision?.id,
      setDivision,
      selectedTeam?.division?.id,
      fullTeamIndex,
      roleKey,
    ]
  )

  // Selecting a team answers the division too — that is the whole point of the
  // teams tab.
  const handleSelectTeam = useCallback(
    (entry: DivisionTeam<Division>) => {
      const isSame =
        teamIdentity(selectedTeam?.team) === teamIdentity(entry.team)

      if (isSame) {
        // Clicking the chosen team again deselects it, matching the division
        // grid. The DIVISION stays: the reader is stepping back from "which
        // team" to "which division", not starting over — and the teams tab is
        // scoped by that division, so dropping it would replace the list they
        // are looking at with every team in the competition.
        setSelectedTeam(null)
        setTeamCleared(true)
        return
      }

      // The team is the whole answer, and it already carries its division —
      // `entry.division`, which `handleNext` reads at submit time.
      //
      // Deliberately NOT `setDivision(entry.division)`. The teams list is
      // scoped by `selectedDivision`, so answering the division here would
      // re-filter the list out from under the reader the instant they clicked:
      // every other team vanishes and the grid jumps. Picking a team is a
      // complete answer, not the first half of one.
      setSelectedTeam(entry)
      setTeamCleared(false)
    },
    [selectedTeam?.team]
  )

  const handleTabChange = useCallback((next: EntityTab) => {
    setTab(next)
    setQuery('')
    // Returning to a tab should show the reader's answer, not the top of a long
    // list, so ask the grid to scroll its selection back into view.
    setScrollKey((key) => key + 1)
  }, [])

  /**
   * Open on the tab that holds the reader's own answer.
   *
   * The two roles finish in different places — `entityAnswered` needs a TEAM
   * from a player and only a DIVISION from a rep — so the tab each one should
   * land on differs too. A player opening on Divisions is looking at the half
   * of the question that will not complete their registration.
   *
   * Only while nothing is chosen. Once a division or team exists, the tab is
   * either something the reader navigated to themselves or one of the three
   * selection-driven `setTab` calls above, and both outrank an opening
   * preference. That guard is also what makes this safe to run on the ordinary
   * standalone pass, where the role is answered before anything else is.
   *
   * Keyed on the role alone: this picks the opening tab when the role is
   * answered and then stays out of the way, so a reader who later switches tabs
   * by hand is not pulled back.
   */
  /**
   * A rep registers a team they are bringing, not one already on the list, so
   * the teams tab is closed to them entirely — forced off it, and any team they
   * arrived holding is dropped rather than left invisible and still submitted.
   *
   * An effect rather than a branch in `setRoleKey`, because this has to catch
   * every way the role can become `team`: a `?type=team` link, a sole role
   * adopted automatically, and a session restore — not only a card press.
   */
  useEffect(() => {
    if (roleKey !== 'team') {
      return
    }
    setTab('divisions')
    if (selectedTeam) {
      setSelectedTeam(null)
      setTeamCleared(true)
    }
  }, [roleKey, selectedTeam])

  /**
   * Apply the competition's configured default once the payload lands.
   *
   * `useState` above can only seed from the first render, where `registration`
   * is still undefined — so without this, a competition that chose Divisions
   * would always open on Teams. Keyed on the resolved value alone, so it fires
   * when the setting arrives and then stays out of the way.
   *
   * Guarded like every other opening-tab rule: silent once the reader has
   * chosen anything, so it cannot pull someone off a tab they navigated to.
   * The role effect below runs after this one and outranks it where both apply.
   */
  useEffect(() => {
    if (selectedDivision || selectedTeam) {
      return
    }
    setTab(defaultTab)
    // `selectedDivision` / `selectedTeam` are read as a guard, not tracked —
    // see the role effect below for why re-running on a selection is wrong.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab])

  useEffect(() => {
    if (!roleKey || selectedDivision || selectedTeam) {
      return
    }
    // A rep always goes to Divisions — the teams tab is not theirs to answer.
    // A player goes wherever the competition points: `defaultTab` is `'teams'`
    // unless an admin chose otherwise, which is the whole point of the setting
    // (SN-8400 — "players will be registering to Divisions primarily"). Without
    // this the setting would be overwritten the instant the role resolved,
    // which for a sole-role competition is immediately.
    setTab(roleKey === 'player' ? defaultTab : 'divisions')
    // `selectedDivision` / `selectedTeam` are read as a guard, not tracked: this
    // fires on the role being answered, and re-running it when a selection
    // lands would fight the auto-advance in `handleSelectDivision`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, defaultTab])

  /**
   * Widen the teams list back to the whole competition.
   *
   * Only the SCOPE goes. The team the reader picked is their answer to this
   * step and survives the list it was found in — dropping it would undo their
   * work to change what they are browsing, and a team is a complete answer on
   * its own now that picking one no longer sets the division.
   *
   * `teamCleared` is deliberately not set: it exists to stop the session
   * restoring a team the reader deliberately removed, and nothing is being
   * removed here.
   */
  const handleClearTeamScope = useCallback(() => {
    setDivision(null)
    setDivisionCleared(true)
  }, [setDivision])

  /**
   * Drop `?team_id=` from the URL.
   *
   * Used when the linked team turns out to be unpickable: leaving the id in
   * place would mean a refresh, or passing the link on again, walks straight
   * back into the same dead end. `withSelectionParams` removes a present param
   * whose value is empty and touches nothing else, so this cannot disturb the
   * division or role params riding alongside it.
   *
   * `replace`, not `push`: it corrects the URL the reader already arrived on
   * rather than adding a history entry that Back would step into.
   */
  const clearTeamParam = useCallback(() => {
    history.replace({
      search: withSelectionParams(
        history.location.search,
        { teamId: null },
        { team: true }
      ),
    })
  }, [history])

  /** Dismiss the full-team notice. The URL and list were already put right. */
  const dismissFullLinkedTeam = useCallback(() => {
    setFullLinkedTeamName(null)
  }, [])

  // Switching role can invalidate the division already chosen; dropping it is
  // better than carrying a stale schedule_id into the session update.
  useEffect(() => {
    if (!roleKey || !selectedDivision) {
      return
    }
    const stillOffered =
      roleKey === 'player'
        ? selectedDivision.is_player_registration
        : selectedDivision.is_team_registration
    if (!stillOffered) {
      setDivision(null)
      setSelectedTeam(null)
      setTeamCleared(true)
    }
    // `setDivision` omitted for the same reason as above — a context setter with
    // a new identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, selectedDivision])

  // A link pre-selects; it does not lock. The step also writes the reader's own
  // choice into these same params on Next, so a locked-looking selection would
  // be indistinguishable from one the reader picked themselves — and coming
  // back to change a choice is the whole reason Back exists. Highlighting the
  // card is enough: the reader still sees what the link chose and still presses
  // Next themselves.
  usePreselectedDivision({
    divisions: roleFilteredDivisions,
    selectedDivision,
    onSelect: setDivision,
  })

  usePreselectedStartTeam({
    teams: registration?.teams,
    selectedTeamId: teamIdentity(selectedTeam?.team) ?? null,
    onSelect: useCallback(
      (entry: DivisionTeam<Division>) => {
        // A link is written once and shared onward, so the team it names may
        // have filled up since. Refuse it rather than selecting something the
        // next step would reject: say why, drop the stale param so it cannot be
        // followed again, and keep the team's DIVISION as the list filter so
        // the reader lands among the nearby alternatives.
        if (linkedTeamOutcome(entry) === 'reject-full') {
          setDivision(entry.division)
          setFullLinkedTeamName(entry.team?.name ?? null)
          clearTeamParam()
          setTab('teams')
          setScrollKey((key) => key + 1)
          return
        }
        // A link that names a team resolves its division too — that is what
        // makes the team reachable, since the teams tab is scoped by division.
        handleSelectTeam(entry)
        // Open on the tab the pre-selection is actually visible in. Teams is
        // already the opening tab, but a team rep's role effect moves to
        // Divisions — which would show the link's division highlighted and the
        // team it named nowhere on screen, leaving the reader to find the other
        // tab to confirm what they are registering for. A link that names a
        // team outranks the role's opening preference.
        setTab('teams')
        setScrollKey((key) => key + 1)
      },
      [handleSelectTeam, clearTeamParam, setDivision]
    ),
  })

  // Restore a division the session already holds — unless the reader has just
  // cleared it. The session is only rewritten on Next, so without that guard a
  // deselect would be undone on the very next render.
  useEffect(() => {
    if (
      !shouldRestoreSessionDivision({
        sessionDivisionId: session?.division_id,
        hasSelection: Boolean(selectedDivision),
        clearedByReader: divisionCleared,
        hasDeepLink: hasSelectionDeepLink,
      })
    ) {
      return
    }
    const restored = filteredDivisions.find(
      (d) => d.schedule_id === session.division_id
    )
    if (restored) {
      setDivision(restored)
    }
    // `setDivision` is intentionally omitted: RegistrationContext defines its
    // setters inline, so each is a NEW function every render. Including one here
    // re-runs this effect on every render, which is half of the update loop this
    // guard exists to prevent. The function's behaviour is stable even though
    // its identity is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session?.division_id,
    selectedDivision,
    divisionCleared,
    hasSelectionDeepLink,
    filteredDivisions,
  ])

  /**
   * Restore a team the session already holds.
   *
   * The division has had this since the start; the team never did, so a reader
   * resuming a session that names a team saw the division come back without it.
   * Resolved from the registration payload's own `teams[]`, the same list the
   * `?team_id=` link uses — no extra request, and it carries the division too.
   */
  useEffect(() => {
    if (
      !shouldRestoreSessionTeam({
        sessionTeamId: session?.team_id,
        hasSelection: Boolean(selectedTeam),
        clearedByReader: teamCleared,
        hasDeepLink: hasSelectionDeepLink,
      })
    ) {
      return
    }
    const restored = findDivisionTeamById(registration?.teams, session.team_id)
    if (restored) {
      // Not `handleSelectTeam`: that lifts the cleared flags and re-answers the
      // division, which is a reader action. This only reinstates what the
      // session already recorded.
      setSelectedTeam(restored)
      // Open on the tab the restored team is actually visible in, matching the
      // `?team_id=` pre-select. A team rep's role effect opens on Divisions,
      // which would show the division highlighted and the team nowhere on
      // screen — a restored team outranks that opening preference.
      setTab('teams')
      setScrollKey((key) => key + 1)
    }
  }, [
    session?.team_id,
    selectedTeam,
    teamCleared,
    hasSelectionDeepLink,
    registration?.teams,
  ])

  // ---- identity ------------------------------------------------------------

  const isTeamRole = roleKey === 'team'

  /**
   * Whether "where" has been answered — and the bar differs by role.
   *
   * A PLAYER joins an existing team, so naming the division is not enough:
   * their registration lands on a specific roster, and they pick it here.
   *
   * A TEAM REP is bringing a team rather than joining one, so the division is
   * the whole answer. Requiring them to pick from the teams already registered
   * would ask for something they may not have yet — and it would make a
   * division with no teams impossible to register into, which is exactly the
   * case a rep is there to fix.
   *
   * A TEAM is a complete answer for either role, on its own: it carries the
   * division that owns it, so nothing is missing. That is why this does not
   * require a division alongside it — the reader who picked a team straight
   * from the teams tab never answered the division separately, and should not
   * have to.
   */
  const entityAnswered =
    Boolean(selectedTeam) || (isTeamRole && Boolean(selectedDivision))

  // ---- readiness -----------------------------------------------------------

  // "Who are you registering?" is asked on register-entity, beside the form
  // that describes that person — so this step is ready once role and entity are
  // answered. A logged-out reader answers the same two and continues to
  // sign-up.
  const canContinue = Boolean(roleKey) && entityAnswered
  const canSubmit = isLogged && canContinue

  // ---- submit --------------------------------------------------------------

  const handleNext = useCallback(async () => {
    // What this step commits, computed ONCE so the session PUT and the context
    // the register step renders from receive the same answer — two copies of
    // the precedence rule could drift apart. The division is the one answered
    // directly, or else the one the chosen team carries (a team-first pick never
    // answers the division — by design, so the list does not re-scope under
    // them); the team is a player's pick, and never a rep's.
    const committed = committedSelection({
      selectedDivision,
      selectedTeam,
      isTeamRole,
      divisions: allDivisions,
    })
    // `schedule_id` is what every downstream step keys off, and it is optional
    // on the type. Without it there is nothing valid to commit.
    const scheduleId = committed.division?.schedule_id
    if (!scheduleId) {
      return
    }
    const teamId = teamIdentity(committed.team)
    setIsSubmitting(true)
    try {
      // Keep EXISTING deep-link params honest — but never add one.
      //
      // The session carries the division, team and role, and every step restores
      // from it, so none of these params is needed to move a choice forward.
      // They matter only because a URL that names them is shareable and may be
      // copied onward: an id left pointing at something the reader has since
      // changed would send the next person to a division, team or role nobody
      // chose. A reader who arrived without them gets a clean URL and keeps it.
      //
      // Replace, not push: it annotates the step the reader is already on rather
      // than adding a history entry they would have to press Back through twice.
      if (hasTeamDeepLink || hasDivisionDeepLink || hasRoleDeepLink) {
        history.replace({
          search: withSelectionParams(
            history.location.search,
            {
              //  Null for a rep, whose commit carries no team: the teams tab
              //  is closed to them, so a lingering id would name a choice they
              //  cannot see.
              teamId: teamId ?? null,
              divisionScheduleId: scheduleId,
              role: roleKey,
            },
            {
              team: hasTeamDeepLink,
              division: hasDivisionDeepLink,
              role: hasRoleDeepLink,
            }
          ),
        })
      }

      // One PUT for everything this step asks. It used to be two — division,
      // then role — which was not atomic: a failure on the second left the
      // session on a division with no role.
      //
      // The role goes with it whether or not the reader is signed in: the types
      // come from `/types/registration`, which needs no account, so a logged-out
      // reader who answered the role question has an answer worth persisting.
      // Only when there is genuinely no role does this fall back to committing
      // the division alone.
      if (role?.id) {
        await updateSessionStart({
          divisionId: scheduleId,
          registrationTypeId: role.id,
          // Undefined for a rep — see `committedSelection`.
          teamId,
          userId: userId ?? undefined,
        })
      } else {
        await updateSessionDivision(scheduleId, userId ?? undefined)
      }

      if (!isLogged) {
        // Pass one ends here: authenticate, then come back to this step.
        setStep(REGISTRATION_STEPS.SIGN_UP)
        return
      }

      checkIfUserIsVerified()
        .then(() => {
          // Hand the commit to the context the register step renders from — the
          // session alone left it holding an earlier pass's team and division.
          // This is the only live forward path into that step: sign-up, login
          // and confirm-email all route back HERE, and the retired select-role
          // screen (which also targets it) is redirected to this step by `Main`
          // before it can act. So this branch alone covers every arrival.
          // Written right before navigating so it lands in the same render as
          // the route change, rather than re-scoping this step's grid while the
          // reader waits.
          setContextSelectedTeam(committed.team)
          setDivision(committed.division)
          setStep(REGISTRATION_STEPS.TEAM_REGISTER)
        })
        .catch(() => setStep(REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL))
    } catch (e: unknown) {
      const apiError = e as SessionUpdateError
      // A dead session cannot be recovered in place.
      if (apiError?.status === 410) {
        deleteSession()
        window.location.reload()
        return
      }
      message.error(
        getErrorMessage(e) ||
          apiError?.invalid_fields?.registration_step ||
          'Something Went Wrong, Please Try Again Later'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [
    selectedDivision,
    // Whole, not `.team` / `.division`: `committedSelection` reads both, and the
    // team-first path commits against the division its team carries — so a
    // change of team is a change of committed division as well as team.
    selectedTeam,
    //  Read by the two `teamId` branches: a rep commits no team.
    isTeamRole,
    roleKey,
    hasTeamDeepLink,
    hasDivisionDeepLink,
    hasRoleDeepLink,
    history,
    updateSessionDivision,
    updateSessionStart,
    userId,
    isLogged,
    role,
    setStep,
    deleteSession,
    message,
    allDivisions,
    setContextSelectedTeam,
    setDivision,
  ])

  /**
   * Logs the reader out and drops the session.
   *
   * This is Logout, not Start Over. They are different actions in the old flow:
   * the division step logs out (there is no progress to discard yet), while
   * later steps offer Start Over, which only resets the session and keeps the
   * reader signed in. This screen is the first step, so Logout is the one that
   * applies.
   */
  const handleLogout = useCallback(() => {
    logout?.()
    resetSession()
    window.location.reload()
  }, [logout, resetSession])

  return {
    // data
    registration,
    email,
    isLogged,
    currencyCode: currencyCode || '',
    divisions: visibleDivisions,
    teamResults,
    nothingAvailable,

    // filters
    ageFilters,
    genderFilters,
    dayOfWeekFilters,
    setSelectedAge,
    setSelectedGender,
    setSelectedDayOfWeek,

    // role
    roleKey,
    soleRole,
    setRoleKey,
    playerAvailable,
    teamAvailable,
    teamFull: isDivisionTeamFull(selectedDivision),

    // entity
    tab,
    query,
    viewMode,
    setViewMode: setChosenViewMode,
    isMobile,
    scrollKey,
    selectedDivision,
    selectedTeam,
    handleTabChange,
    setQuery,
    handleSelectDivision,
    handleSelectTeam,
    handleClearTeamScope,

    // a `?team_id=` link that named a full team
    fullLinkedTeamName,
    dismissFullLinkedTeam,

    // identity
    setSelectedPlayer,
    isTeamRole,

    // submit
    entityAnswered,
    canContinue,
    canSubmit,
    isSubmitting,
    handleNext,
    handleLogout,
  }
}
